/* The retry key for "create a ticket".
 *
 * The server saves a request once per key (api/src/shared/operation.js). So:
 *   - re-sending the SAME request (a stutter, a double click) must carry the SAME
 *     key, and the server answers with the ticket it already made;
 *   - a request the person has CHANGED since a failed attempt must carry a NEW
 *     key, or the server refuses it: same key, different words.
 *
 * A draft remembers the key and what the last attempt looked like. Pure on
 * purpose: no React, no storage, so the rule is testable. The key is a UUID;
 * `makeId` is injectable for tests. */

const defaultId = () => crypto.randomUUID();

export function newDraft(makeId = defaultId) {
  return { key: makeId(), signature: null };
}

// Same content in a different key order is the same request.
function canonical(value) {
  if (Array.isArray(value)) return `[${value.map(canonical).join(',')}]`;
  if (value && typeof value === 'object') {
    return `{${Object.keys(value).sort().map((k) => `${JSON.stringify(k)}:${canonical(value[k])}`).join(',')}}`;
  }
  return JSON.stringify(value === undefined ? null : value);
}

/** The key to send for this attempt, and the draft to keep for the next one. */
export function keyForAttempt(draft, request, makeId = defaultId) {
  // Who it is for is part of the request: the same answers for a different person are a new ticket.
  const signature = canonical({ requestTypeId: request.requestTypeId, formData: request.formData, note: request.note, forUpn: request.forUpn || null });
  const base = draft.signature !== null && draft.signature !== signature ? newDraft(makeId) : draft;
  return { key: base.key, draft: { key: base.key, signature } };
}
